import client from './client';
import { fetchAllPages } from './fetchAllPages';
import { applyApprovedOnlyParams, applyScopedParams } from './viewerAccess';
import { extractCollectionResponse } from './collectionResponse';
import {
  getSharedRecords,
  mergeRecordsByUuid,
  removeSharedRecord,
  upsertSharedRecord,
  upsertSharedRecords,
} from './sharedRecordCache';
import { withOfflineCollectionSupport } from './offlineCollectionSupport';

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isNaN(parsed) ? null : parsed;
}

function cleanQueryValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const text = String(value).trim();
  return text ? text : null;
}

function buildQueryParams(params = {}, defaults = {}) {
  const query = { ...defaults };

  Object.entries(applyScopedParams(applyApprovedOnlyParams(params))).forEach(([key, value]) => {
    const cleaned = cleanQueryValue(value);
    if (cleaned === null || cleaned === undefined) return;
    query[key] = cleaned;
  });

  return query;
}

function unwrapResponse(responseData) {
  return responseData?.data ?? responseData ?? {};
}

function extractCollection(responseData, normalize) {
  return extractCollectionResponse(responseData, normalize);
}

function extractItem(responseData, normalize, targetUuid) {
  const payload = unwrapResponse(responseData);
  const candidates = [payload, payload?.data];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (Array.isArray(candidate.items)) {
      const match = targetUuid
        ? candidate.items.find((item) => item?.uuid === targetUuid)
        : candidate.items[0];

      if (match) return normalize(match);
    }

    if (
      candidate.uuid ||
      candidate.certificate_number ||
      candidate.certificateNumber ||
      candidate.state ||
      candidate.species_uuid
    ) {
      return normalize(candidate);
    }
  }

  return normalize({});
}

function textKey(value) {
  return String(value ?? '').trim().toLowerCase();
}

function matchesVetCertificateFilters(row = {}, params = {}) {
  const speciesCandidates = [
    row.species_uuid,
    row.speciesUuid,
    ...(Array.isArray(row.species_uuids) ? row.species_uuids : []),
    ...(Array.isArray(row.speciesUuids) ? row.speciesUuids : []),
  ];

  const checks = [
    ['state', [row.state]],
    ['species_uuid', speciesCandidates],
    ['purpose', [row.purpose]],
  ];

  return checks.every(([paramKey, candidates]) => {
    const filterValue = textKey(params[paramKey]);
    if (!filterValue) return true;
    return candidates.some((candidate) => textKey(candidate) === filterValue);
  });
}

function makeCollectionApi({ basePath, normalize, buildPayload, defaultPerPage = 25 }) {
  async function fetchPage(params = {}) {
    const { data } = await client.get(basePath, {
      params: buildQueryParams(params, {
        per_page: params.per_page ?? defaultPerPage,
        page: params.page ?? 1,
      }),
    });
    return extractCollection(data, normalize);
  }

  return {
    async list(params = {}) {
      return fetchPage(params);
    },

    async listAll(params = {}) {
      return fetchAllPages((page) => fetchPage({ ...params, page }));
    },

    async get(uuid) {
      const { data } = await client.get(`${basePath}/${uuid}`);
      return extractItem(data, normalize, uuid);
    },

    async create(payload) {
      const { data } = await client.post(basePath, buildPayload(payload));
      return extractItem(data, normalize);
    },

    async update(uuid, payload) {
      const { data } = await client.put(`${basePath}/${uuid}`, buildPayload(payload));
      return extractItem(data, normalize, uuid);
    },

    async remove(uuid) {
      const { data } = await client.delete(`${basePath}/${uuid}`);
      return data;
    },

    async submit(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/submit`);
      return extractItem(data, normalize, uuid);
    },

    async validate(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/validate`);
      return extractItem(data, normalize, uuid);
    },

    async approve(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/approve`);
      return extractItem(data, normalize, uuid);
    },

    async reject(uuid, notes = '') {
      const { data } = await client.post(`${basePath}/${uuid}/reject`, { notes });
      return extractItem(data, normalize, uuid);
    },
  };
}

function normalizeVetCertificate(row = {}) {
  const marketObj = row.origin_market && typeof row.origin_market === 'object' ? row.origin_market : {};
  const speciesObj = row.species && typeof row.species === 'object' ? row.species : {};
  const institutionObj =
    row.issuing_institution && typeof row.issuing_institution === 'object'
      ? row.issuing_institution
      : {};

  const originMarketUuid =
    row.origin_market_uuid ??
    marketObj.uuid ??
    marketObj.id ??
    null;

  const speciesUuid =
    row.species_uuid ??
    speciesObj.uuid ??
    speciesObj.id ??
    null;

  const speciesCode =
    row.species_code ??
    row.speciesCode ??
    speciesObj.code ??
    speciesObj.species_code ??
    '';

  const speciesLabel =
    row.species_label ??
    row.speciesLabel ??
    speciesObj.common_name_nigeria ??
    speciesObj.commonNameNigeria ??
    speciesObj.name ??
    speciesObj.label ??
    speciesCode ??
    speciesUuid ??
    '';

  const issuingInstitutionUuid =
    row.issuing_institution_uuid ??
    institutionObj.uuid ??
    institutionObj.id ??
    null;

  const authorityObj =
    row.issuing_authority && typeof row.issuing_authority === 'object'
      ? row.issuing_authority
      : {};

  const issuingAuthorityUuid =
    row.issuing_authority_uuid ?? authorityObj.uuid ?? authorityObj.id ?? null;

  const issuingAuthorityLabel =
    authorityObj.name ?? authorityObj.label ?? issuingAuthorityUuid ?? '';

  const issuingInstitutionLabel =
    institutionObj.name ?? institutionObj.label ?? issuingInstitutionUuid ?? '';

  const originMarketLabel =
    marketObj.market_name ?? marketObj.name ?? originMarketUuid ?? '';

  const status = row.status ?? row.validation_status ?? row.workflow_status ?? '';

  const speciesUuidsRaw = row.species_uuids ?? row.speciesUuids ?? null;
  const speciesUuids = Array.isArray(speciesUuidsRaw)
    ? speciesUuidsRaw
    : speciesUuid ? [speciesUuid] : [];

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    certificate_number: row.certificate_number ?? row.certificateNumber ?? '',
    certificateNumber: row.certificate_number ?? row.certificateNumber ?? '',
    issued_at: row.issued_at ?? row.issuedAt ?? '',
    issuedAt: row.issued_at ?? row.issuedAt ?? '',
    expires_at: row.expires_at ?? row.expiresAt ?? '',
    expiresAt: row.expires_at ?? row.expiresAt ?? '',
    issuing_officer_name: row.issuing_officer_name ?? row.issuingOfficerName ?? '',
    issuingOfficerName: row.issuing_officer_name ?? row.issuingOfficerName ?? '',
    issuing_institution_uuid: issuingInstitutionUuid,
    issuingInstitutionUuid,
    issuing_institution_label: issuingInstitutionLabel,
    issuing_authority_uuid: issuingAuthorityUuid,
    issuingAuthorityUuid,
    issuing_authority_label: issuingAuthorityLabel,
    origin_market_uuid: originMarketUuid,
    originMarketUuid,
    origin_market_label: originMarketLabel,
    state: row.state ?? '',
    species_uuid: speciesUuid,
    speciesUuid,
    species_uuids: speciesUuids,
    speciesUuids,
    species_code: speciesCode,
    speciesCode,
    species_label: speciesLabel,
    speciesLabel,
    head_count: toNumberOrNull(row.head_count ?? row.headCount),
    headCount: toNumberOrNull(row.head_count ?? row.headCount),
    destination: row.destination ?? '',
    purpose: row.purpose ?? '',
    notes: row.notes ?? '',
    status,
    status_label: status,
    border_crossing_uuid: row.border_crossing_uuid ?? row.borderCrossingUuid ?? null,
    reason_for_revocation: row.reason_for_revocation ?? row.reasonForRevocation ?? null,
    data_status: row.data_status ?? row.dataStatus ?? row.workflow_status ?? 'draft',
    dataStatus: row.data_status ?? row.dataStatus ?? row.workflow_status ?? 'draft',
    validation_status: row.validation_status ?? '',
    validationStatus: row.validation_status ?? '',
    workflow_status: row.workflow_status ?? '',
    workflowStatus: row.workflow_status ?? '',
    is_active: row.is_active ?? null,
    isActive: row.is_active ?? null,
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildVetCertificatePayload(payload = {}) {
  const source = payload.raw ?? payload.existing ?? payload.original ?? {};
  const speciesUuids =
    Array.isArray(payload.species_uuids) && payload.species_uuids.length > 0
      ? payload.species_uuids
      : [trimOrNull(payload.species_uuid ?? payload.speciesUuid ?? source.species_uuid ?? source.speciesUuid)].filter(Boolean);

  const status = trimOrNull(payload.status ?? source.status ?? source.validation_status ?? source.workflow_status);
  const dataStatus = trimOrNull(
    payload.data_status ??
    payload.dataStatus ??
    source.data_status ??
    source.dataStatus ??
    source.workflow_status ??
    'draft'
  ) ?? 'draft';

  return {
    certificate_number:       trimOrNull(payload.certificate_number ?? payload.certificateNumber ?? source.certificate_number ?? source.certificateNumber),
    issued_at:                trimOrNull(payload.issued_at ?? payload.issuedAt ?? source.issued_at ?? source.issuedAt),
    expires_at:               trimOrNull(payload.expires_at ?? payload.expiresAt ?? source.expires_at ?? source.expiresAt),
    issuing_officer_name:     trimOrNull(payload.issuing_officer_name ?? payload.issuingOfficerName ?? source.issuing_officer_name ?? source.issuingOfficerName),
    issuing_institution_uuid: trimOrNull(payload.issuing_institution_uuid ?? payload.issuingInstitutionUuid ?? source.issuing_institution_uuid ?? source.issuingInstitutionUuid),
    issuing_authority_uuid:   trimOrNull(payload.issuing_authority_uuid ?? payload.issuingAuthorityUuid ?? source.issuing_authority_uuid ?? source.issuingAuthorityUuid) ?? null,
    origin_market_uuid:       trimOrNull(payload.origin_market_uuid ?? payload.originMarketUuid ?? source.origin_market_uuid ?? source.originMarketUuid),
    state:                    trimOrNull(payload.state ?? source.state),
    species_uuid:             speciesUuids[0] ?? null,
    species_uuids:            speciesUuids,
    head_count:               toNumberOrNull(payload.head_count ?? payload.headCount ?? source.head_count ?? source.headCount),
    destination:              trimOrNull(payload.destination ?? source.destination),
    purpose:                  trimOrNull(payload.purpose ?? source.purpose),
    status,
    border_crossing_uuid:     trimOrNull(payload.border_crossing_uuid ?? payload.borderCrossingUuid ?? source.border_crossing_uuid ?? source.borderCrossingUuid) ?? null,
    data_status:              dataStatus,
    reason_for_revocation:    status === 'revoked'
      ? (trimOrNull(payload.reason_for_revocation ?? payload.reasonForRevocation ?? source.reason_for_revocation ?? source.reasonForRevocation) ?? null)
      : null,
    notes:                    trimOrNull(payload.notes ?? source.notes),
  };
}

const vetCertificatesBaseApi = makeCollectionApi({
  basePath: '/v1/vet-certificates',
  normalize: normalizeVetCertificate,
  buildPayload: buildVetCertificatePayload,
  defaultPerPage: 25,
});

const VET_CERTIFICATES_CACHE_KEY = 'vet-certificates';

function cacheVetCertificates(rows = []) {
  upsertSharedRecords(VET_CERTIFICATES_CACHE_KEY, rows);
}

function filterVetCertificatesByParams(rows = [], params = {}) {
  return rows.filter((row) => matchesVetCertificateFilters(row, params));
}

const vetCertificatesLegacyApi = {
  ...vetCertificatesBaseApi,

  async listAll(params = {}) {
    const live = await vetCertificatesBaseApi.listAll(params);
    cacheVetCertificates(live.items ?? []);
    const cached = filterVetCertificatesByParams(getSharedRecords(VET_CERTIFICATES_CACHE_KEY), params);
    return {
      ...live,
      items: mergeRecordsByUuid(live.items ?? [], cached),
    };
  },

  async get(uuid) {
    try {
      const record = await vetCertificatesBaseApi.get(uuid);
      if (record?.uuid) upsertSharedRecord(VET_CERTIFICATES_CACHE_KEY, record);
      return record;
    } catch (error) {
      const cached = getSharedRecords(VET_CERTIFICATES_CACHE_KEY).find(
        (row) => String(row?.uuid ?? row?.id ?? '').trim() === String(uuid ?? '').trim()
      );
      if (cached) return normalizeVetCertificate(cached);
      throw error;
    }
  },

  async create(payload) {
    const record = await vetCertificatesBaseApi.create(payload);
    if (record?.uuid) upsertSharedRecord(VET_CERTIFICATES_CACHE_KEY, record);
    return record;
  },

  async update(uuid, payload) {
    const record = await vetCertificatesBaseApi.update(uuid, payload);
    if (record?.uuid) upsertSharedRecord(VET_CERTIFICATES_CACHE_KEY, record);
    return record;
  },

  async remove(uuid) {
    const response = await vetCertificatesBaseApi.remove(uuid);
    removeSharedRecord(VET_CERTIFICATES_CACHE_KEY, uuid);
    return response;
  },

  async submit(uuid) {
    const record = await vetCertificatesBaseApi.submit(uuid);
    if (record?.uuid) upsertSharedRecord(VET_CERTIFICATES_CACHE_KEY, record);
    return record;
  },

  async validate(uuid) {
    const record = await vetCertificatesBaseApi.validate(uuid);
    if (record?.uuid) upsertSharedRecord(VET_CERTIFICATES_CACHE_KEY, record);
    return record;
  },

  async approve(uuid) {
    const record = await vetCertificatesBaseApi.approve(uuid);
    if (record?.uuid) upsertSharedRecord(VET_CERTIFICATES_CACHE_KEY, record);
    return record;
  },

  async reject(uuid, notes = '') {
    if (!notes || !notes.trim()) throw new Error('Rejection reason is required.');
    const record = await vetCertificatesBaseApi.reject(uuid, notes.trim());
    if (record?.uuid) upsertSharedRecord(VET_CERTIFICATES_CACHE_KEY, record);
    return record;
  },
};

export const vetCertificatesApi = withOfflineCollectionSupport(vetCertificatesLegacyApi, {
  basePath: '/v1/vet-certificates',
  collectionKey: 'vet-certificates',
  normalize: normalizeVetCertificate,
  buildPayload: buildVetCertificatePayload,
});

export { normalizeVetCertificate, buildVetCertificatePayload };
